import { UserContext } from "App";
import SessionsApi from "Classes/API/Administration/SessionsApi";
import SearchStringOrIntReq from "Classes/Models/Requests/SearchStringOrIntReq";
import PayFromBalanceReq from "Classes/Models/Requests/Accounting/PayFromBalanceReq";
import AccountingApi from "Classes/API/AccountingApi";
import SessionsTable from "layouts/AccountingSessions/SessionsTable";
import { useContext } from "react";
import Swal from "sweetalert2";

export default function PackageSessionsTable({ packageData, setIsLoaded, setPackageData }) {
  const CurrentUser = useContext(UserContext);

  const refundSession = async (sessions) => {
    if (!sessions || sessions.length === 0) return;

    const sessionList = Array.isArray(sessions) ? sessions : [sessions];

    const refundableSessions = sessionList.filter((s) => s.isPaidFor && !s.isAdmitted);
    const nonRefundableSessions = sessionList.filter((s) => !s.isPaidFor || s.isAdmitted);

    const refundableCount = refundableSessions.length;
    const nonRefundableCount = nonRefundableSessions.length;

    let title = "";
    let text = "";

    if (sessionList.length === 1) {
      title = refundableCount ? "Refund this session?" : "This session cannot be refunded.";
      text = refundableCount
        ? "The session funds will be returned to the package."
        : nonRefundableSessions[0].isAdmitted
        ? "This session has been admitted and cannot be refunded."
        : "Only paid sessions can be refunded.";
    } else {
      if (refundableCount < sessionList.length) {
        title = `${sessionList.length} sessions selected, but only ${refundableCount} can be refunded.`;
        text = `Refund ${refundableCount} refundable session(s)? (${nonRefundableCount} cannot be refunded.)`;
      } else {
        title = `Refund ${refundableCount} selected sessions?`;
        text = "All selected sessions will be refunded to the package balance.";
      }
    }

    const confirmation = await Swal.fire({
      title,
      text,
      icon: refundableCount > 0 ? "warning" : "info",
      showCancelButton: refundableCount > 0,
      confirmButtonColor: "#f0ad4e",
      cancelButtonColor: "#d33",
      confirmButtonText: refundableCount > 0 ? "Yes, Refund" : "Ok",
    });

    if (!confirmation.isConfirmed || refundableCount === 0) return;

    // Refund request
    const req = new PayFromBalanceReq();
    req.accountingSessionIds = refundableSessions.map((s) => s.id);
    req.packageId = packageData.id;
    req.paymentType = "PackageSessionReturn";

    try {
      await AccountingApi.PayFromBalance(req);
      await Swal.fire({
        title: "Refund Completed",
        text: `${refundableCount} session${refundableCount > 1 ? "s" : ""} refunded successfully.`,
        icon: "success",
        confirmButtonText: "Ok",
      });
      window.location.reload();
    } catch (err) {
      console.error("Refund error:", err);
      Swal.fire({
        title: "Error",
        text: "Refund failed for all selected sessions.",
        icon: "error",
        confirmButtonText: "Ok",
      });
    }
  };

  const AbsentButtonClick = async (selectedSessions) => {
    const alreadyAbsent = selectedSessions.filter((s) => s.isAbsent === true);
    const notAbsent = selectedSessions.filter((s) => !s.isAbsent);

    const totalSelected = selectedSessions.length;
    const absentCount = alreadyAbsent.length;
    const notAbsentCount = notAbsent.length;

    if (absentCount === 0) {
      Swal.fire(
        "No Absent Sessions",
        "None of the selected sessions are marked as absent. Nothing to update.",
        "info"
      );
      return;
    }

    let messageHtml = `
    <div style="text-align: left;">
      <p><strong>Total Selected:</strong> ${totalSelected}</p>
      <p><strong>Marked as Absent:</strong> ${absentCount}</p>
  `;

    if (notAbsentCount > 0) {
      messageHtml += `
      <p style="color:gray;"><strong>Not Absent:</strong> ${notAbsentCount}</p>
      <p style="color:red;">⚠️ Only absent sessions will be updated.</p>
    `;
    }

    messageHtml += `
      <p style="margin-top:10px;">Are you sure you want to <strong>remove absent status</strong> from 
      <b>${absentCount}</b> session${absentCount > 1 ? "s" : ""}?</p>
    </div>
  `;
    const confirmation = await Swal.fire({
      title: "Confirm Unabsent Action",
      html: messageHtml,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, update ${absentCount} session${absentCount > 1 ? "s" : ""}`,
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ffcc00",
      cancelButtonColor: "#d33",
    });

    if (!confirmation.isConfirmed) return;
    let successCount = 0;
    let failCount = 0;
    try {
      const req = new SearchStringOrIntReq();
      req.SearchIntegers = alreadyAbsent.map((s) => s.id);
      const res = await SessionsApi.ToggleAccountingSessionIsAbsent(req);

      if (res.status === "Success") successCount++;
      else failCount++;
    } catch (err) {
      failCount++;
      console.error("Error toggling absent:", err);
    }
    Swal.fire({
      title: "Operation Complete",
      html: `
      <div style="text-align: left;">
        <p><strong>Total Selected:</strong> ${totalSelected}</p>
        <p><strong>Processed:</strong> ${absentCount}</p>
        <p style="color:green;"><strong>Successful:</strong> ${successCount}</p>
        <p style="color:red;"><strong>Failed:</strong> ${failCount}</p>
      </div>
    `,
      icon: successCount > 0 ? "success" : "error",
      confirmButtonText: "OK",
    }).then(() => {
      if (successCount > 0) window.location.reload();
    });
  };
  const DeleteSessions = async (sessions) => {
    if ((!["Admin", "Owner", "HeadAccounting"].includes(CurrentUser.userType))) {
      await Swal.fire({
        title: "Error",
        text: "You are not authorized to delete accounting sessions",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmation.value) return;

    Swal.fire({
      title: "Loading...",
      text: "Please wait",
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      icon: "info",
    });

    const sessionList = Array.isArray(sessions) ? sessions : [sessions];
    const req = new SearchStringOrIntReq();
    req.SearchIntegers = sessionList.map((s) => s.id);

    const res = await SessionsApi.DeleteAccountingSession(req);

    if (res.status === "Success") {
      setPackageData((oldPackage) => ({
        ...oldPackage,
        getAccountingSessions: oldPackage.getAccountingSessions.filter(
          (accS) => !sessionList.some((s) => s.id === accS.id)
        ),
      }));

      await Swal.fire({
        title: "Deleted Successfully",
        text: `Deleted ${sessionList.length} session(s) successfully`,
        icon: "success",
      });
    } else {
      await Swal.fire({
        title: "Delete Failed",
        text: res.message || "Failed to delete accounting sessions",
        icon: "error",
      });
    }
  };

  const unabsentAndRefundSessions = async (selectedSessions) => {
    if (!selectedSessions || selectedSessions.length === 0) return;

    const alreadyAbsent = selectedSessions.filter((s) => s.isAbsent === true);
    const notAbsent = selectedSessions.filter((s) => !s.isAbsent);

    const refundableSessions = selectedSessions.filter((s) => s.isPaidFor && !s.isAdmitted);
    const nonRefundableSessions = selectedSessions.filter((s) => s.isAdmitted || !s.isPaidFor);

    if (refundableSessions.length === 0) {
      Swal.fire({
        title: "ERROR",
        text: "No Sessions to Unabsent or Refund",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    const totalSelected = selectedSessions.length;
    const absentCount = alreadyAbsent.length;
    const notAbsentCount = notAbsent.length;
    const refundableCount = refundableSessions.length;
    const nonRefundableCount = nonRefundableSessions.length;

    let messageHtml = `
    <div style="text-align: left;">
      <p><strong>Total Selected:</strong> ${totalSelected}</p>
      <p><strong>Marked as Absent:</strong> ${absentCount}</p>
      <p><strong>Refundable:</strong> ${refundableCount}</p>
  `;

    if (notAbsentCount > 0) {
      messageHtml += `
      <p style="color:gray;"><strong>Not Absent:</strong> ${notAbsentCount}</p>
      <p style="color:red;">⚠️ Only absent sessions will be unabsented.</p>
    `;
    }

    if (nonRefundableCount > 0) {
      messageHtml += `
      <p style="color:gray;"><strong>Cannot Refund:</strong> ${nonRefundableCount} session(s) are admitted or unpaid.</p>
    `;
    }

    messageHtml += `
      <p style="margin-top:10px;">
        Are you sure you want to <strong>remove absent status</strong> from <b>${absentCount}</b> session${
      absentCount > 1 ? "s" : ""
    } 
        and <strong>refund</strong> <b>${refundableCount}</b> session${
      refundableCount > 1 ? "s" : ""
    }?
      </p>
    </div>
  `;

    const confirmation = await Swal.fire({
      title: "Confirm Unabsent & Refund",
      html: messageHtml,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, proceed`,
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ffcc00",
      cancelButtonColor: "#d33",
    });

    if (!confirmation.isConfirmed) return;

    // Process unabsent sessions
    let unabsentSuccess = 0;
    let unabsentFail = 0;

    if (alreadyAbsent.length > 0) {
      try {
        const req = new SearchStringOrIntReq();
        req.SearchIntegers = alreadyAbsent.map((s) => s.id);
        const res = await SessionsApi.ToggleAccountingSessionIsAbsent(req);

        if (res.status === "Success") unabsentSuccess += alreadyAbsent.length;
        else unabsentFail += alreadyAbsent.length;
      } catch (err) {
        unabsentFail += alreadyAbsent.length;
        console.error("Error toggling absent:", err);
      }
    }

    // Process refund sessions
    let refundSuccess = 0;
    let refundFail = 0;

    if (refundableSessions.length > 0) {
      const req = new PayFromBalanceReq();
      req.accountingSessionIds = refundableSessions.map((s) => s.id);
      req.packageId = packageData.id;
      req.paymentType = "PackageSessionReturn";

      try {
        await AccountingApi.PayFromBalance(req);
        refundSuccess += refundableSessions.length;
      } catch (err) {
        refundFail += refundableSessions.length;
        console.error("Refund error:", err);
      }
    }

    // Show final result
    Swal.fire({
      title: "Operation Complete",
      html: `
      <div style="text-align: left;">
        <p><strong>Total Selected:</strong> ${totalSelected}</p>
        <p><strong>Unabsented:</strong> ${unabsentSuccess}</p>
        <p style="color:red;"><strong>Unabsent Failed:</strong> ${unabsentFail}</p>
        <p><strong>Refunded:</strong> ${refundSuccess}</p>
        <p style="color:red;"><strong>Refund Failed:</strong> ${refundFail}</p>
        <p style="color:gray;"><strong>Could not refund:</strong> ${nonRefundableCount}</p>
      </div>
    `,
      icon: unabsentSuccess + refundSuccess > 0 ? "success" : "error",
      confirmButtonText: "OK",
    }).then(() => {
      if (unabsentSuccess + refundSuccess > 0) window.location.reload();
    });
  };

  return (
    <SessionsTable
      accountingSessions={packageData.getAccountingSessions}
      setIsLoaded={setIsLoaded}
      refundSession={refundSession}
      AbsentButtonClick={AbsentButtonClick}
      DeleteSessions={DeleteSessions}
      unabsentAndRefundSessions={unabsentAndRefundSessions}
      type="Package"
    />
  );
}
